import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useCallback, useEffect } from 'react';
import { I as InlinePortalProvider } from '../Portal/InlinePortalProvider-260ac00a.js';
import { a as Shroud, b as SheetContentContainer, c as SheetContentSurface, d as SheetContent } from './Sheet.styles-e1be9e70.js';
import { C as CloseButtonSection } from './CloseButtonSection-afcdb705.js';
import { b as SurfaceLight, S as SurfaceDarkInfo, a as SurfaceDark } from '../Surface/index-945bfaeb.js';
import { k as keys } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-04a50e15-b033061a.js';
import { t as trapTabFocus } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/trapTabFocus-c2676751-9f3167b4.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-73f83f90-0ab4a5e4.js';
import { i as isAndroidFirefox$1 } from '../LocalModules/storm-ui-utils/dist/esm/isAndroidFirefox-f5997692-ba631597.js';

function getSurfaceByType(type) {
  switch (type) {
    case 'dark':
      return SurfaceDark;
    case 'blue':
      return SurfaceDarkInfo;
    default:
      return SurfaceLight;
  }
}
function createSetBottomToVisualViewport(containerRef) {
  const isAndroidFirefox = isAndroidFirefox$1();
  return function setBottomToVisualViewport() {
    if (typeof window !== 'undefined' && 'visualViewport' in window && window.visualViewport) {
      const {
        height: viewportHeight
      } = window.visualViewport;
      if (containerRef && containerRef.current instanceof HTMLDivElement) {
        const {
          height: containerHeight
        } = containerRef.current.getBoundingClientRect();
        let offset = 0;
        /**
        * Work-around for Android Firefox bug which causes 56px of whitespace
        * at the bottom of the screen when the Sheet is opened. On resize, if
        * the visualViewport height is greater than the outerHeight of the window, we
        * assume the whitespace is visible and add 56px to the bottom of the Sheet
        * to account for the whitespace blocking any content. Will remove once the
        * browser bug is fixed.
        */
        if (isAndroidFirefox && viewportHeight > window.outerHeight) {
          /**
           * Handle the Android Firefox bug that shifts the body up by 56px when the nav bar is hidden
           */
          offset = -56;
        }
        containerRef.current.style.setProperty('top', `${viewportHeight - containerHeight + offset}px`);
        containerRef.current.style.setProperty('bottom', 'auto');
      }
    }
  };
}
const Overlay = ({
  children,
  className,
  closeButton,
  closeButtonProps,
  modalContainerRef,
  onClose,
  padding = 'base',
  side = 'bottom',
  state,
  type = 'light',
  withCloseButton,
  maxSize = 'default',
  header,
  ...rest
}) => {
  const closeRef = useRef(null);
  const handleKeyUp = useCallback(event => {
    if (event.key === keys.ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
      if (onClose) {
        onClose();
      }
    }
  }, [onClose]);
  const handleKeyDown = useCallback(event => {
    if (modalContainerRef.current !== null) {
      trapTabFocus(event, modalContainerRef.current);
    }
  }, [modalContainerRef]);
  const handleClick = useCallback(event => {
    const {
      target,
      currentTarget
    } = event;
    if (target === currentTarget) {
      event.preventDefault();
      if (onClose) {
        onClose();
      }
    }
  }, [onClose]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (modalContainerRef.current) {
      const focusableEls = getFocusableElements(modalContainerRef.current);
      if (focusableEls.length > 0) {
        focusableEls[0].focus();
      }
    }
    if (typeof window !== 'undefined' && 'visualViewport' in window && window.visualViewport) {
      const {
        visualViewport
      } = window;

      /**
       * To set the bottom of the sheet to viewport
       * Useful when virtual keyboard is opened
       * Sheet will be pushed up so the keyboard won't cover any content
       */
      const setBottomToVisualViewport = createSetBottomToVisualViewport(modalContainerRef);
      visualViewport.addEventListener('resize', setBottomToVisualViewport);
      setBottomToVisualViewport();
      return () => {
        visualViewport.removeEventListener('resize', setBottomToVisualViewport);
      };
    }
  }, [modalContainerRef]);
  return /*#__PURE__*/React__default.createElement(Shroud, {
    className: className,
    onMouseDown: handleClick,
    onKeyUp: handleKeyUp,
    onKeyDown: handleKeyDown,
    role: "presentation",
    ref: modalContainerRef,
    $fadeState: state
  }, /*#__PURE__*/React__default.createElement(InlinePortalProvider, null, /*#__PURE__*/React__default.createElement(SheetContentContainer, {
    $slideState: state,
    side: side
  }, withCloseButton && /*#__PURE__*/React__default.createElement(CloseButtonSection, {
    onClose: onClose,
    closeRef: closeRef,
    closeButtonProps: closeButtonProps,
    closeButton: closeButton,
    side: side
  }), /*#__PURE__*/React__default.createElement(SheetContentSurface, {
    as: getSurfaceByType(type),
    $padding: padding,
    side: side,
    $maxSize: maxSize
  }, header, /*#__PURE__*/React__default.createElement(SheetContent, _extends({}, rest, {
    $padding: padding
  }), children)))));
};
Overlay.defaultProps = {
  className: undefined,
  handleClick: undefined,
  handleKeyUp: undefined,
  handleKeyDown: undefined,
  onClose: undefined,
  closeButtonProps: undefined,
  padding: 'base',
  side: 'bottom',
  type: 'light',
  withCloseButton: false,
  maxSize: 'default',
  header: undefined
};

export { Overlay as O };
//# sourceMappingURL=Overlay-a3632f61.js.map
